import { QuestionPreviewData } from "@vericus/cadmus-common";

import {
  BaseType,
  Cardinality,
  FieldOutcomeFragment,
  QuestionFieldFragment,
  QuestionType,
  ResponseDeclarationFragment,
} from "@/generated/graphql";

/**
 * This file contains functions to explicitly map graphql types to json schema
 * types
 */

export function mapQuestionType(
  questionType: QuestionType
): QuestionPreviewData["question_type"] {
  switch (questionType) {
    case QuestionType.Mcq:
      return "mcq";
    case QuestionType.Matching:
      return "matching";
    case QuestionType.Short:
      return "short";
    case QuestionType.Truefalse:
      return "truefalse";
    case QuestionType.Blanks:
      return "blanks";
    case QuestionType.Numerical:
      return "numerical";
    case QuestionType.Overview:
      return "overview";
    case QuestionType.Sub:
      return "sub";
    case QuestionType.Hotspot:
      return "hotspot";
    default:
      return "extended";
  }
}

export function mapFieldInteraction(
  interaction: QuestionFieldFragment["interaction"]
) {
  switch (interaction.__typename) {
    case "ChoiceInteraction":
      return {
        kind: "choice_interaction" as const,
        label: interaction.label,
        choices: interaction.choices,
        max_choices: interaction.maxChoices,
      };

    case "TextEntryInteraction":
      return {
        kind: "text_entry_interaction" as const,
        expected_length: interaction.expectedLength,
        base_type: mapBaseType(interaction.baseType),
      };

    case "MatchInteraction":
      return {
        kind: "match_interaction" as const,
        target_set: interaction.targetSet,
        source_set: interaction.sourceSet,
      };

    case "PositionObjectInteraction":
      return {
        kind: "position_object_interaction" as const,
        label: interaction.label,
        max_choices: interaction.maxChoices,
      };

    case "ExtendedInteraction":
    default:
      return {
        kind: "extended_interaction" as const,
        word_limit: null,
      };
  }
}

export function mapFieldResponse(response: ResponseDeclarationFragment | null) {
  return {
    match_similarity: response?.matchSimilarity ?? null,
    match_phrases: response?.correctValues ?? null,
    partial_scoring: response?.partialScoring ?? null,
    case_sensitive: response?.caseSensitive ?? null,
    correct_values: response?.correctValues ?? [],
    correct_ranges: response?.correctRanges ?? [],
    correct_areas: response?.correctAreas ?? [],
    cardinality: mapCardinality(response?.cardinality),
    base_type: mapBaseType(response?.baseType),
    error_margin: response?.errorMargin ?? null,
  };
}

export function mapCardinality(cardinality: Cardinality | null | undefined) {
  switch (cardinality) {
    case Cardinality.Multiple:
      return "multiple" as const;
    case Cardinality.Ordered:
      return "ordered" as const;
    case Cardinality.Single:
      return "single" as const;
    default:
      return "single" as const;
  }
}

function mapBaseType(baseType: BaseType | null | undefined) {
  switch (baseType) {
    case BaseType.DirectedPair:
      return "directed_pair" as const;
    case BaseType.Identifier:
      return "identifier" as const;
    case BaseType.Record:
      return "record" as const;
    case BaseType.String:
      return "string" as const;
    case BaseType.Range:
      return "range" as const;
    case BaseType.Float:
      return "float" as const;
    default:
      return "string" as const;
  }
}

export function mapFieldOutcome(fieldOutcome: FieldOutcomeFragment) {
  return {
    identifier: fieldOutcome.identifier,
    answer: fieldOutcome.answer,
    answers: fieldOutcome.answers,
    automark_score: fieldOutcome.automarkScore,
  };
}
